import { Dayjs } from 'dayjs'

import { day, getOnlyDate, getWeekdaysBetween, isAfter, isBefore } from '~/astra'
import { useFlag } from '~/hooks/useFlag'
import { selectLocationValues as selectOPLocationValues } from '~/store/slices/operationalPlannerFilterSlice'
import { selectLocationValues as selectSurgeonSchedulerLocationValues } from '~/store/slices/surgeonSchedulerFilterSlice'
import { selectLocationValues as selectSurgerySchedulerLocationValues } from '~/store/slices/surgerySchedulerFilterSlice'
import { StoreState, useStore } from '~/store/store'

import {
    Include,
    Location,
    selectLocations,
    selectSpecialities,
    StormQueryOfPlural,
    useGetOpeningHours,
    useLocation,
    useLocations,
    useLocationScheduleSpecialities,
} from '../'

export const ACTIVE_LOCATION_ORDER = ['room_code'] as const

/** Sentinel values for optional singular lookups — never match a real location. */
export const NO_MATCH_LOCATION_ID = -1
export const NO_MATCH_ROOM_CODE = '__NO_ROOM_CODE__'

export const BOOKING_LOCATION_INCLUDE = {
    dipsResource: true,
} as const

export type BookingLocation = Include<Location, typeof BOOKING_LOCATION_INCLUDE>

export function selectBookingLocations(state: StoreState) {
    return selectLocations(state, { orderBy: ACTIVE_LOCATION_ORDER, include: BOOKING_LOCATION_INCLUDE })
}

export const HOSPITAL_CONFIG_LOCATION_INCLUDE = {
    departments: true,
    dipsResource: true,
    locationProfiles: {
        locationSurgeryTypeGroups: { surgeryTypeGroup: true },
    },
} as const

export type HospitalConfigLocation = Include<Location, typeof HOSPITAL_CONFIG_LOCATION_INCLUDE>

export function useAllLocationsForHospitalConfig() {
    return useLocations({
        orderBy: ACTIVE_LOCATION_ORDER,
        include: HOSPITAL_CONFIG_LOCATION_INCLUDE,
    })
}

/** All locations sorted by room_code. */
export function useOrderedLocations(query?: StormQueryOfPlural<typeof useLocations>) {
    return useLocations({
        ...query,
        orderBy: query?.orderBy ?? ACTIVE_LOCATION_ORDER,
    })
}

/** Singular lookup by id; pass undefined to get undefined (uses sentinel id). */
export function useLocationById(locationId: number | undefined) {
    return useLocation({ where: { id: locationId ?? NO_MATCH_LOCATION_ID } })
}

type ActiveLocationsArgs = {
    // startDate and endDate should be required when the migration to specialty allocation is completed
    startDate?: Dayjs
    endDate?: Dayjs
    query?: StormQueryOfPlural<typeof useLocations>
}

/** Returns the location schedule specialities for the selected specialities within the given date range. */
function useSelectedLocationScheduleSpecialities({ startDate, endDate }: Pick<ActiveLocationsArgs, 'startDate' | 'endDate'>) {
    const selectedSpecialties = useStore(state => state.appFilters.selectedSpecialties)
    const allSpecialties = useStore(selectSpecialities)
    const selectedSpecialtiesUuids = selectedSpecialties === 'all' ? allSpecialties.map(speciality => speciality.uuid) : selectedSpecialties

    return useLocationScheduleSpecialities({
        where: {
            speciality_uuid: { in: selectedSpecialtiesUuids },
            scheduled_date: { gte: getOnlyDate(startDate ?? day(0)), lte: getOnlyDate(endDate ?? day(0)) },
        },
    })
}

/**
 * Hook to get active locations based on the selected specialities (if the feature is enabled), otherwise based on the selected departments.
 *
 * If enabled, we check the allocations (i.e. fagområde) plan within the given date range, and return the locations that have allocations for the selected specialities.
 * Otherwise, we return the locations based on the deprecated department-location relationship.
 */
export function useActiveLocations({ startDate, endDate, query }: ActiveLocationsArgs) {
    if (query?.where && 'departments' in query.where) {
        console.warn('useActiveLocations: `departments` filter in `query.where` is deprecated, as this relationship will be removed soon.')
    }

    const isSpecialtiesSelectionEnabled = useFlag('use_specialties_selection')
    const hasDateRange = startDate && endDate

    // The new way: getting locations via selected specialities
    const locationScheduleSpecialities = useSelectedLocationScheduleSpecialities({ startDate, endDate })
    const specialtyLocationIds = [...new Set(locationScheduleSpecialities.map(lss => lss.location_id))]

    function getSpecialtyWhere() {
        if (specialtyLocationIds.length === 0) return { id: NO_MATCH_LOCATION_ID }
        if (query?.where) return { AND: [{ id: { in: specialtyLocationIds } }, query.where] }
        return { id: { in: specialtyLocationIds } }
    }

    // The old way: getting locations via selected departments
    const departmentKey = useStore(state => state.appFilters.departmentKey)
    const departmentWhere = departmentKey === 'all' ? undefined : { departments: { any: { id: departmentKey } } }

    function getDepartmentWhere() {
        if (departmentWhere && query?.where) return { AND: [departmentWhere, query.where] }
        return query?.where ?? departmentWhere
    }

    // Using the right `where` clause depending on the feature flag and its dependencies
    const shouldUseSpecialtyWhere = isSpecialtiesSelectionEnabled && hasDateRange
    const locationWhere = shouldUseSpecialtyWhere ? getSpecialtyWhere() : getDepartmentWhere()

    return useLocations({
        ...query,
        where: locationWhere,
        orderBy: query?.orderBy ?? ACTIVE_LOCATION_ORDER,
    })
}

/** Returns a lookup function to check if an OR-day is allocated to the selected specialities within the date range (if the feature is enabled). */
export function useORDaysAllocatedToSelectedSpecialties({ startDate, endDate }: Required<Pick<ActiveLocationsArgs, 'startDate' | 'endDate'>>) {
    const isSpecialtiesSelectionEnabled = useFlag('use_specialties_selection')
    const locationScheduleSpecialities = useSelectedLocationScheduleSpecialities({ startDate, endDate })

    const allocatedDatesByLocationId = new Map<number, Set<string>>()

    for (const lss of locationScheduleSpecialities) {
        const locationId = lss.location_id
        const date = getOnlyDate(lss.scheduled_date)

        const dates = allocatedDatesByLocationId.get(locationId)

        if (dates) {
            dates.add(date)
        } else {
            allocatedDatesByLocationId.set(locationId, new Set([date]))
        }
    }

    const isORDayAllocatedToSelectedSpecialties = (locationId: number, date: Dayjs) => {
        if (!isSpecialtiesSelectionEnabled) return true

        const isDateOutsideRange = isBefore(date, startDate, 'day') || isAfter(date, endDate, 'day')
        if (isDateOutsideRange) {
            console.warn('isORDayAllocatedToSelectedSpecialties: `date` arg is outside the given date range.')
        }

        const isUnknownLocation = !allocatedDatesByLocationId.has(locationId)
        if (isUnknownLocation) {
            console.warn('isORDayAllocatedToSelectedSpecialties: `locationId` arg is not an active location for the selected specialities.')
        }

        if (isDateOutsideRange || isUnknownLocation) return true

        return allocatedDatesByLocationId.get(locationId)?.has(getOnlyDate(date)) ?? false
    }

    return { isORDayAllocatedToSelectedSpecialties }
}

function useActiveLocationsFilteredByUiValues(activeLocationsArgs: ActiveLocationsArgs, locationValues: string[]) {
    const activeLocations = useActiveLocations(activeLocationsArgs)

    // Return all if no filter is applied
    if (!locationValues.length) return activeLocations

    return activeLocations.filter(location => locationValues.includes(location.id.toString()))
}

export function useOPFilteredLocations(activeLocationsArgs: ActiveLocationsArgs) {
    const locationValues = useStore(selectOPLocationValues)
    return useActiveLocationsFilteredByUiValues(activeLocationsArgs, locationValues)
}

export function useSurgeonSchedulerFilteredLocations(activeLocationsArgs: ActiveLocationsArgs) {
    const locationValues = useStore(selectSurgeonSchedulerLocationValues)
    return useActiveLocationsFilteredByUiValues(activeLocationsArgs, locationValues)
}

export function useSurgerySchedulerFilteredLocations(activeLocationsArgs: ActiveLocationsArgs) {
    const locationValues = useStore(selectSurgerySchedulerLocationValues)
    return useActiveLocationsFilteredByUiValues(activeLocationsArgs, locationValues)
}

/** Ids for Storm `{ in: … }` filters. Id-only call sites only — when you already use `use*FilteredLocations`, derive ids from that result instead to avoid a second subscription. */
export function useSurgerySchedulerFilteredLocationIds(dateArgs: Pick<ActiveLocationsArgs, 'startDate' | 'endDate'>) {
    return useSurgerySchedulerFilteredLocations(dateArgs).map(location => location.id)
}

// Used for block schedule batch form
export function useSurgerySchedulerLocationOptions() {
    return useLocations().map(location => ({ value: location.id, label: location.room_code }))
}

/**
 * Returns locations open on at least one weekday in the date range, per the opening-hours rule (`getOpeningHours`).
 * Reads location service overrides from the store without loading them; `useOpenLocationsInDateRangeQuery` loads them first.
 *
 * Weekdays without a service period count as open, since `getOpeningHours` falls back to default hours for them.
 * Weekends are skipped: without a service period they resolve as not closed, which would count every location as open.
 */
export function useOpenLocationsInDateRange(startDate?: Dayjs | string, endDate?: Dayjs | string) {
    const locations = useOrderedLocations()
    const getOpeningHours = useGetOpeningHours()

    const isOpenAnyWeekdayBetween = (start: Dayjs | string, end: Dayjs | string) => {
        const weekdays = getWeekdaysBetween(day(start), day(end))
        return (location: Location) => weekdays.some(date => !getOpeningHours(location, date).isClosed)
    }

    if (!startDate || !endDate) return []

    return locations.filter(isOpenAnyWeekdayBetween(startDate, endDate))
}
